<template>
    <FilterBase :title>
        <InputRangeTwoKnobbedWithInputs
            v-model="calculatedModel"
            :data-qa-max="bounds.max"
            :data-qa-min="bounds.min"
            :data-qa-value="JSON.stringify(calculatedModel)"
            :debounce="500"
            :min="Number(props.bounds.min.amount)"
            :max="Number(props.bounds.max.amount)"
            :value-formatter="formatter"
            :min-label
            :max-label
        />
    </FilterBase>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import type {
    NonSkuPriceRangeFragment,
    NonSkuPriceRangeInput,
} from '@/graphql/api'

import FilterBase from '@/components/ui/filter/filters/layout/FilterBase.vue'
import type {
    InputRangeModel,
    InputRangeTwoKnobbedValueFormatter,
} from '@/components/ui/inputs/range/input-range-two-knobbed-props.ts'
import InputRangeTwoKnobbedWithInputs from '@/components/ui/inputs/range/InputRangeTwoKnobbedWithInputs.vue'
import { moneyInputOrMoneyFragmentAmount } from '@/domain'
import { useCurrencyInput } from '@/plugins/currency-input.ts'
import { useMoneyFormatter } from '@/plugins/money-formatter.ts'
import { useCurrency } from '@/plugins/currency.ts'

const props = withDefaults(
    defineProps<{
        modelValue?: NonSkuPriceRangeInput | null | undefined
        bounds: NonSkuPriceRangeFragment
        title?: string
        minLabel: string
        maxLabel: string
    }>(),
    {
        modelValue: null,
        title: undefined,
    }
)

const emit = defineEmits<{
    (e: 'update:modelValue', v: NonSkuPriceRangeInput): void
}>()

const currencyInput = useCurrencyInput()
const currency = useCurrency()

const calculatedModel = computed({
    get(): InputRangeModel {
        return [
            moneyInputOrMoneyFragmentAmount(
                props.modelValue?.min,
                props.bounds.min
            ),
            moneyInputOrMoneyFragmentAmount(
                props.modelValue?.max,
                props.bounds.max
            ),
        ]
    },
    set(v: InputRangeModel) {
        const min = v[0]
        const max = v[1]

        emit('update:modelValue', {
            min: min
                ? {
                      amount: min.toString(),
                      currency: currencyInput.value,
                  }
                : null,
            max: max
                ? {
                      amount: max.toString(),
                      currency: currencyInput.value,
                  }
                : null,
        })
    },
})

const moneyFormatter = useMoneyFormatter()

const formatter: InputRangeTwoKnobbedValueFormatter = (value) =>
    moneyFormatter({
        amount: value.toString(),
        currency: currency.value,
    })
</script>
